import * as THREE from 'three';
import { TILE, tileKey } from '../../shared/geo.js';
import { MeshBuilder } from './meshbuilder.js';
import { paintGround, buildTerrain, disposeGround } from './terrain.js';
import { addBuildings } from './buildings.js';
import { addRoads, addWater } from './roads.js';
import { buildTrees } from './trees.js';
import { propLists, addWires, addLampHeads, propMeshes } from './props.js';
import { SOLID, GLASS } from './buildings.js';
import { bucketColliders, roadCells, surfaceAt, wet, collidersNear } from './tileindex.js';
import { freeze } from './cells.js';
import { footprint } from './buildings.js';

const MAX_FETCHES = 4;
const BUILD_BUDGET = 10; // milliseconds of tile building per frame (at least one tile)

// Keeps the tiles around the player loaded and everything else freed.
// Each tile is one ground mesh plus one merged mesh per material.
export class Chunks {
  // map: { meta, heightmap, loadTile(key) }; materials: { walls, roofs, tar, earth, water, detail }
  constructor(scene, map, materials) {
    this.scene = scene;
    this.map = map;
    this.materials = materials;
    this.radius = 3; // tiles drawn in each direction
    this.tiles = new Map(); // key -> { tx, tz, state, data, group, colliders }
    this.fetching = 0;
    this.known = new Set(map.meta.tiles);
    this.hole = { value: new THREE.Vector4(0, 0, 0, 0) }; // area the far terrain leaves to the tiles
    this.trees = true; // plant trees (off on the low quality setting)
    this.onTileBuilt = null;
    this.onTileRemoved = null;
    this.lamps = null; // { x, z, radius }: the town centre, where streetlights stand
    this.skips = {}; // tile key -> [building indices] a landmark model stands for
  }

  get viewDistance() {
    return this.radius * TILE;
  }

  inBounds(tx, tz) {
    const b = this.map.meta.bounds;
    return (tx + 1) * TILE > b.x0 && tx * TILE < b.x1 && (tz + 1) * TILE > b.z0 && tz * TILE < b.z1;
  }

  // Call every frame with the player's position.
  update(px, pz) {
    const cx = Math.floor(px / TILE);
    const cz = Math.floor(pz / TILE);
    const r = this.radius;

    // free tiles that fell out of range (one extra ring of slack avoids churn)
    for (const [key, t] of this.tiles) {
      if (Math.abs(t.tx - cx) > r + 1 || Math.abs(t.tz - cz) > r + 1) {
        this.dispose(t);
        this.tiles.delete(key);
      }
    }

    // request missing tiles, nearest first
    const wanted = [];
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        const tx = cx + dx;
        const tz = cz + dz;
        if (!this.inBounds(tx, tz)) continue;
        const key = tileKey(tx, tz);
        if (!this.tiles.has(key)) wanted.push({ tx, tz, key, d: dx * dx + dz * dz });
      }
    }
    wanted.sort((a, b) => a.d - b.d);
    for (const w of wanted) {
      const t = { tx: w.tx, tz: w.tz, key: w.key, state: 'loading', data: null, group: null, colliders: [] };
      this.tiles.set(w.key, t);
      if (!this.known.has(w.key)) {
        t.state = 'ready';
        continue;
      }
      this.queueFetch(t);
    }

    // build a little each frame so loading never freezes the game
    const started = performance.now();
    let complete = true;
    const ready = [];
    for (const t of this.tiles.values()) {
      if (t.state === 'ready') ready.push(t);
      if (t.state !== 'built' && Math.abs(t.tx - cx) <= r && Math.abs(t.tz - cz) <= r) complete = false;
    }
    ready.sort((a, b) => (a.tx - cx) ** 2 + (a.tz - cz) ** 2 - ((b.tx - cx) ** 2 + (b.tz - cz) ** 2));
    for (const t of ready) {
      this.build(t);
      if (performance.now() - started > BUILD_BUDGET) break;
    }

    // the far terrain is hidden only where detailed tiles fully cover it
    // While the next ring loads, the previous square is still fully built
    // (tiles are kept one ring beyond the radius), so its hole stays valid.
    if (complete) {
      this.setHole(cx, cz, r);
    } else if (this.holeX === undefined || Math.abs(cx - this.holeX) > 1 || Math.abs(cz - this.holeZ) > 1) {
      // far from the last complete square (first load or a jump): open the
      // largest square around the player whose tiles are all built
      let size = -1;
      for (let rr = 0; rr <= r; rr++) {
        let all = true;
        for (let dz = -rr; dz <= rr && all; dz++) {
          for (let dx = -rr; dx <= rr; dx++) {
            if (!this.inBounds(cx + dx, cz + dz)) continue;
            const t = this.tiles.get(tileKey(cx + dx, cz + dz));
            if (!t || t.state !== 'built') {
              all = false;
              break;
            }
          }
        }
        if (!all) break;
        size = rr;
      }
      if (size >= 0) this.setHole(cx, cz, size);
      else this.hole.value.set(0, 0, 0, 0);
      this.holeX = undefined;
    }
    return complete;
  }

  setHole(cx, cz, r) {
    const m = 40;
    this.hole.value.set((cx - r) * TILE + m, (cz - r) * TILE + m, (cx + r + 1) * TILE - m, (cz + r + 1) * TILE - m);
    this.holeX = cx;
    this.holeZ = cz;
  }

  queueFetch(t) {
    if (this.fetching >= MAX_FETCHES) {
      t.state = 'queued';
      return;
    }
    this.fetching++;
    t.state = 'loading';
    this.map.loadTile(t.key).then(
      (data) => {
        t.data = data;
        if (t.state === 'loading') t.state = 'ready';
      },
      () => {
        if (t.state === 'loading') t.state = 'ready';
      }
    ).finally(() => {
      this.fetching--;
      for (const o of this.tiles.values()) {
        if (o.state === 'queued') {
          this.queueFetch(o);
          if (this.fetching >= MAX_FETCHES) break;
        }
      }
    });
  }

  build(t) {
    const { heightmap } = this.map;
    const m = this.materials;
    const group = new THREE.Group();
    group.add(buildTerrain(t.tx, t.tz, heightmap, paintGround(t.data, t.tx, t.tz, heightmap), m.detail, !!m.nodes));
    if (t.data) {
      const walls = new MeshBuilder(true);
      const roofs = new MeshBuilder();
      const tar = new MeshBuilder();
      const earth = new MeshBuilder();
      const water = new MeshBuilder();
      const skip = this.skips[t.key] ? new Set(this.skips[t.key]) : null;
      t.colliders = addBuildings(t.data, t.tx, t.tz, heightmap, walls, roofs, null, skip);
      // a skipped building still blocks the way
      if (skip) for (const i of skip) if (t.data.b[i] && t.data.b[i].p.length >= 6) t.colliders.push(footprint(t.data.b[i], t.tx, t.tz, heightmap));
      t.buckets = bucketColliders(t.colliders);
      addRoads(t.data, t.tx, t.tz, heightmap, tar, earth);
      t.roadCells = roadCells(t.data, t.tx, t.tz);
      addWater(t.data, t.tx, t.tz, heightmap, water);
      const lists = propLists(t.data, t.tx, t.tz, heightmap, null, null, this.lamps);
      walls.lit = 0;
      addWires(walls, lists.wires, { r: 0.12, g: 0.12, b: 0.13 }, SOLID);
      walls.lit = 1;
      addLampHeads(walls, lists.heads, { r: 1, g: 0.95, b: 0.8 }, GLASS);
      walls.lit = 0;
      const props = propMeshes(lists, t.tx * TILE, t.tz * TILE);
      if (props) group.add(props);
      const add = (builder, material, cast, receive) => {
        if (builder.empty) return;
        const mesh = new THREE.Mesh(builder.geometry(), material);
        mesh.position.set(t.tx * TILE, 0, t.tz * TILE);
        mesh.castShadow = cast;
        mesh.receiveShadow = receive;
        group.add(mesh);
      };
      add(walls, m.walls, true, true);
      add(roofs, m.roofs, true, true);
      add(tar, m.tar, false, true);
      add(earth, m.earth, false, true);
      add(water, m.water, false, false);
    }
    const trees = this.trees ? buildTrees(t.data, t.tx, t.tz, heightmap) : null;
    if (trees) group.add(trees);
    freeze(group);
    t.group = group;
    t.state = 'built';
    this.scene.add(group);
    if (this.onTileBuilt) this.onTileBuilt(t);
  }

  dispose(t) {
    if (t.state === 'loading' || t.state === 'queued') t.state = 'dropped';
    if (!t.group) return;
    this.scene.remove(t.group);
    t.group.traverse((o) => {
      if (!o.isMesh) return;
      if (o.isInstancedMesh) {
        // trees share their shape and material; only the instance data is the tile's
        o.dispose();
        return;
      }
      o.geometry.dispose();
      // the ground material and its picture belong to the tile; the rest are shared
      if (o.userData.ownsMaterial) disposeGround(o.material);
    });
    if (this.onTileRemoved) this.onTileRemoved(t);
    t.group = null;
  }

  surfaceAt(x, z) {
    return surfaceAt(this.tiles, x, z);
  }

  wet(x, z, margin = 0) {
    return wet(this.map, this.tiles, x, z, margin);
  }

  collidersNear(x, z, out) {
    return collidersNear(this.tiles, x, z, out);
  }
}
